import React, { useEffect, useState } from 'react';

// Mock PWA functionality for TypeScript compatibility
const useRegisterSW = (_options?: {
  onRegisteredSW?: (swUrl: string, r: ServiceWorkerRegistration) => void;
  onRegisterError?: (error: any) => void;
}) => {
  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);

  const updateServiceWorker = async (reloadPage?: boolean) => {
    if (reloadPage) window.location.reload();
  };

  return {
    offlineReady: [offlineReady, setOfflineReady] as [boolean, (value: boolean) => void],
    needRefresh: [needRefresh, setNeedRefresh] as [boolean, (value: boolean) => void],
    updateServiceWorker
  };
};

const PWAUpdatePrompt: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const {
    offlineReady: [offlineReady, setOfflineReady],
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl: string, r: ServiceWorkerRegistration) {
      console.log('SW Registered: ' + r);
      // Check for updates every hour
      r && setInterval(() => {
        r.update();
      }, 60000 * 60);
    },
    onRegisterError(error: any) {
      console.log('SW registration error', error);
    },
  });

  useEffect(() => {
    if (needRefresh) {
      setUpdateAvailable(true);
    }
  }, [needRefresh]);

  const close = () => {
    setOfflineReady(false);
    setNeedRefresh(false);
    setUpdateAvailable(false);
  };

  const handleUpdate = () => {
    updateServiceWorker(true);
  };

  if (!offlineReady && !needRefresh && !updateAvailable) {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm">
      {offlineReady && (
        <div className="bg-green-600 text-white p-4 rounded-lg shadow-lg border border-green-700">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-200 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">App ready to work offline</p>
              <p className="text-xs text-green-200 mt-1">
                Your app is now cached and will work without an internet connection.
              </p>
            </div>
            <button
              onClick={close}
              className="flex-shrink-0 text-green-200 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {(needRefresh || updateAvailable) && (
        <div className="bg-blue-600 text-white p-4 rounded-lg shadow-lg border border-blue-700">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-200 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">New version available</p>
              <p className="text-xs text-blue-200 mt-1">
                Click reload to update to the latest version for the best experience.
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleUpdate}
                  className="bg-blue-700 hover:bg-blue-800 text-white text-xs px-3 py-1.5 rounded transition-colors font-medium"
                >
                  Reload
                </button>
                <button
                  onClick={close}
                  className="text-blue-200 hover:text-white text-xs px-3 py-1.5 rounded transition-colors"
                >
                  Later
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PWAUpdatePrompt;